import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { CirclePlus, CircleMinus } from "lucide-react";

import { createNewIntentPromptsAPI, fetchSingleIntentPromptsAPI, fetchCustomFieldsAPI } from "../../../services/intent_prompt";

const CreateIntentPrompt = ({ type }) => {
  let { id } = useParams();
  if (type) id = type;

  let navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [intentPrompt, setIntentPrompt] = useState(null);
  const [customFields, setCustomFields] = useState(null);
  const [customFieldsSelected, setCustomFieldsSelected] = useState([]);
  const [addFields, setAddFields] = useState(false);
  const [searchCustomFields, setSearchCustomFields] = useState("");
  const [modelsLoading, setModelsLoading] = useState(false);
  const [llmModels, setLlmModels] = useState([]);

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      id: intentPrompt?.id,
      name: type ? type : "",
      llm_model: type ? type : "meta-llama/Meta-Llama-3.1-8B-Instruct",
      type: type ? type : "call",
      prompt_text: "",
    },
  });

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchDataFromApi = async () => {
    try {
      if (id) {
        const responseData = await fetchSingleIntentPromptsAPI(id);
        setIntentPrompt(responseData.data);
      }
    } catch (error) {
      // setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomFieldsFromApi = async () => {
    try {
      const responseData = await fetchCustomFieldsAPI();
      setCustomFields(responseData.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchLlmModels = async () => {
    try {
      setModelsLoading(true);
      const response = await fetch(`https://api.deepinfra.com/v1/models`, {
        method: "GET",
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message);
      }
      if (data && data.data) {
        // return data.data;
        setLlmModels(data.data);
      }
    } catch (error) {
      throw error;
    } finally {
      setModelsLoading(false);
    }
  };

  useEffect(() => {
    fetchLlmModels();
    fetchDataFromApi();
    fetchCustomFieldsFromApi(); // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    reset({
      id: id,
      llm_model: type ? type : intentPrompt?.llm_model,
      name: type ? type : intentPrompt?.name,
      type: type ? type : intentPrompt?.type,
      prompt_text: intentPrompt?.prompt_text,
    });

    if (!type && intentPrompt?.prompt_fields) {
      let selectedFields = intentPrompt.prompt_fields.split("|");
      setCustomFieldsSelected(selectedFields);
    } // eslint-disable-next-line
  }, [intentPrompt, reset]);

  const onSubmit = async (data) => {
    // console.log("data to submit", data);
    setLoading(true);
    // call server api here
    try {
      data["prompt_fields"] = customFieldsSelected;
      const responseData = await createNewIntentPromptsAPI(data);
      if (responseData && responseData.status === "success") {
        setSuccess(responseData);

        if (!type) {
          return navigate(`/dashboard/intent-prompt/save/${responseData?.data?.id}`);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const addOrRemoveField = (item, addremove) => {
    let previousFields = customFieldsSelected;
    previousFields = previousFields.filter((itm) => itm !== item.fieldKey);
    if (addremove === "add") {
      previousFields.push(item.fieldKey);
    }
    setCustomFieldsSelected(previousFields);
  };

  const names = {
    "faq-prompt": "for FAQ",
    "objections-prompt": "for Objections",
  };

  // const modelTypes = [
  //   { id: "meta-llama/Meta-Llama-3.1-8B-Instruct", name: "Llama-3.1-8B-Instruct", context: "128k" }, // new version
  //   // { id: "meta-llama/Meta-Llama-3-8B-Instruct", name: "Llama-3-8B-Instruct", context: "8k" }, // older version

  //   { id: "mistralai/Mistral-7B-Instruct-v0.3", name: "Mistral-7B-Instruct-v0.3", context: "32k" },
  //   // ---
  //   { id: "Gryphe/MythoMax-L2-13b", name: "MythoMax-L2-13b", context: "4k" },
  //   // ---
  //   // { id: "meta-llama/Meta-Llama-3.1-70B-Instruct", name: "Llama-3.1-70B-Instruct", context: "128k" }, //older version
  //   // { id: "meta-llama/Llama-3.1-8B-Instruct", name: "Llama-3.1-8B-Instruct", context: "128k" }, //new version

  //   // { id: "meta-llama/Meta-Llama-3-70B-Instruct", name: "Llama-3-70B-Instruct", context: "8k" }, // older version
  //   { id: "meta-llama/Llama-3.3-70B-Instruct", name: "Llama-3.3-70B-Instruct", context: "128k" }, // new version
  // ];

  const allowedModels = [
    { name: "deepseek-ai", label: "Deepseek" },
    { name: "Qwen", label: "Qwen" },
    { name: "meta-llama", label: "Llama" },
    { name: "google", label: "Google" },
    { name: "nvidia", label: "Nvidia" },
    { name: "anthropic", label: "Claude" },
    { name: "microsoft", label: "Microsoft" },
    { name: "mistralai", label: "Mistralai" },
    { name: "gryphe", label: "Grype" },
    { name: "black-forest-labs", label: "Flux" },
    // { name: "BAAI", label: "AI" },
  ];

  return (
    <div className="flex flex-row gap-4 px-10 pb-24">
      <div className=" flex items-center justify-center w-full">
        <div className=" w-full">
          <form className="space-y-4 " onSubmit={handleSubmit(onSubmit)}>
            <div className="flex justify-end">
              <div className="flex flex-col mt-5">
                {/* <div>
                <AssistantDropDown singlePrompt={singlePrompt} />
              </div> */}
                <button
                  type="submit"
                  className={`group flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
  ${loading ? "bg-gray-400 cursor-not-allowed" : "hover:bg-blue-700 focus:ring-blue-500"}`}
                >
                  Save Changes
                </button>
                <div className="flex justify-center">
                  {!!loading && <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-10 w-10 opacity-90  mt-4"></div>}
                  {!!error && !loading && <div className="text-center text-red-500">{error?.message}</div>}
                  {!loading && <div className="pt-2 text-green-600">{success?.message}</div>}
                </div>
                {/* <div>
                <AssistantDropDown singlePrompt={singlePrompt} />
              </div> */}
              </div>
            </div>

            <div className="flex flex-row w-full gap-4">
              <div className="w-full">
                {!type && (
                  <>
                    <div className="">
                      <label htmlFor="" className="">
                        Type
                      </label>
                      <select id="typedropdown" className="text-white  block w-full px-3 py-3  my-4 border  bg-gray-900  rounded-md  outline-none border-gray-800 focus:outline-none  focus:ring-indigo-500 focus:border-indigo-500  sm:text-sm" {...register("type")}>
                        <option value="call">Call</option>
                        <option value="sms">SMS</option>
                        <option value="auto_reply_sms">AutoReply SMS</option>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="space-y-2 mt-6">
                      <label htmlFor="" className="">
                        Name
                      </label>
                      <input id="name" name="name" type="text" required {...register("name")} className="relative block w-full px-3 py-3 border  bg-gray-900  rounded-md  outline-none border-gray-800 focus:outline-none  focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" />
                    </div>

                    <div className="space-y-2 mt-6">
                      <label htmlFor="llmmodeldropdown">LLM Model</label>
                      <select id="llmmodeldropdown" className={`text-white block w-full px-3 py-3 my-4 border bg-gray-900 rounded-md outline-none border-gray-800 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${modelsLoading ? "opacity-50 cursor-not-allowed" : ""}`} {...register("llm_model")} disabled={modelsLoading}>
                        {modelsLoading ? (
                          <option>Loading models...</option>
                        ) : (
                          Array.from(new Set(allowedModels.map((m) => m.label))).map((category) => {
                            const categoryModels = llmModels.filter((model) => allowedModels.some((m) => m.label === category && model.id.toLowerCase().startsWith(m.name.toLowerCase())));

                            if (!categoryModels.length) return null;

                            return (
                              <optgroup key={category} label={category}>
                                {categoryModels.map((model) => (
                                  <option key={model.id} value={model.id}>
                                    {model.id}
                                  </option>
                                ))}
                              </optgroup>
                            );
                          })
                        )}
                      </select>
                    </div>
                  </>
                )}

                <div className="space-y-2 mt-6">
                  <label htmlFor="" className="">
                    LLM Prompt Text {!!type ? names[type] : ""}
                  </label>
                  <textarea id="prompt_text" name="prompt_text" type="text" required {...register("prompt_text")} className="relative block w-full px-3 py-3 border  bg-gray-900  rounded-md  outline-none border-gray-800 focus:outline-none  focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" cols={4} rows={6} />
                </div>

                {!type && !!customFields && (
                  <div className="space-y-2 mt-6">
                    <label htmlFor="" className="">
                      Response Update Fields:
                    </label>
                    <div>
                      <div className="flex justify-between items-end">
                        <div className="space-y-2 mt-6">
                          <label htmlFor="" className="">
                            Search
                          </label>
                          <input
                            type="text"
                            value={searchCustomFields}
                            onChange={(e) => {
                              setSearchCustomFields(e.target.value);
                            }}
                            className="relative block w-full px-3 py-3 border  bg-gray-900  rounded-md  outline-none border-gray-800 focus:outline-none  focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <button
                            onClick={() => {
                              setAddFields(!addFields);
                            }}
                            type="button"
                            className={`group flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                          >
                            {!addFields ? "Show Fields" : "Hide Fields"}
                          </button>
                        </div>
                      </div>
                      {!!addFields && (
                        <div className="py-3 my-10 rounded-md bg-gray-900 border-gray-800 h-96 overflow-auto grid grid-cols-[auto_1fr]">
                          {customFields
                            .filter((field) => field.name.toLowerCase().includes(searchCustomFields.toLowerCase()))
                            .map((oneField) => (
                              <>
                                <div key={oneField.id} className={"px-3 py-2 " + (customFieldsSelected.indexOf(oneField.fieldKey) > -1 ? "text-gray-600 cursor-default" : "hover:bg-gray-800")}>
                                  <CirclePlus
                                    className={"inline mr-3 " + (customFieldsSelected.indexOf(oneField.fieldKey) > -1 ? "" : "cursor-pointer")}
                                    size="20"
                                    onClick={() => {
                                      addOrRemoveField(oneField, "add");
                                    }}
                                  />
                                  {oneField.name}
                                </div>
                                <div key={"1" + oneField.id} className="px-3 py-2">
                                  {oneField.fieldKey}
                                </div>
                              </>
                            ))}
                        </div>
                      )}

                      <div className="grid grid-cols-[auto_1fr]">
                        {customFields.map((oneField) =>
                          customFieldsSelected.indexOf(oneField.fieldKey) > -1 ? (
                            <>
                              <div key={"sel" + oneField.id} className="px-3 py-2">
                                <CircleMinus
                                  className="inline mr-3"
                                  size="20"
                                  onClick={() => {
                                    addOrRemoveField(oneField, "remove");
                                  }}
                                />
                                {oneField.name}
                              </div>
                              <div key={"sel1" + oneField.id} className="px-5 py-2">
                                {oneField.fieldKey}
                              </div>
                            </>
                          ) : (
                            <></>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateIntentPrompt;
